import type { FilterOption } from '@/components/common-table/FilterSearchBar'
import { URL_CHARACTER_LIMIT } from '@/config/constants'
import { decodeFilters, encodeFilters, filterLimitFlags, getRouteSection } from '@/lib/filterUtils'
import { useLocation, useRouter, useSearch } from '@tanstack/react-router'
import { isEqual } from 'lodash-es'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

// Constants for session storage
const SECTION_STORAGE_KEY = 'platform_ui_last_filter_section'

// Define a broad search type that includes our filters parameter
interface SearchWithFilters extends Record<string, unknown> {
	filters?: string
}

/**
 * Hook to synchronize filter state with the URL query parameters.
 * Persists filters on refresh and clears them when navigating between major sections.
 *
 * @param filters Current applied filters
 * @param onFiltersChange Callback to update filters in parent state
 * @param insideModal Whether the search bar is used inside a modal (disables URL sync)
 */
export function useUrlFilters(
	filters: FilterOption[],
	onFiltersChange?: (filters: FilterOption[]) => void,
	insideModal = false,
) {
	const { navigate } = useRouter()
	const location = useLocation()
	const search = useSearch({ strict: false }) as SearchWithFilters

	const currentPathname = location.pathname

	// Tracks if initial URL reading is complete, unlocking the sync effect
	const [isInitialized, setIsInitialized] = useState(false)
	// Protects against empty local state on mount wiping out existing URL filters
	const isFirstSyncRef = useRef(true)
	// Ensures the initial URL reading effect only executes once
	const initialLoadRef = useRef(false)
	// Tracks if the URL limit warning has been shown
	const hasWarnedRef = useRef(false)

	// 1. Initial Load: Read from URL on mount
	// NOTE: onFiltersChange must be a stable reference to avoid infinite loops.
	// Uses useLayoutEffect to fire BEFORE parent useEffect callbacks (e.g. fetchDevices),
	// ensuring the filtered search starts first and the loading state is set early.
	useLayoutEffect(() => {
		if (initialLoadRef.current || insideModal || !onFiltersChange) {
			if (!isInitialized) {
				setIsInitialized(true)
			}
			return
		}
		initialLoadRef.current = true

		// Use window.location.search to get raw data immediately on mount
		const searchParams = new URLSearchParams(window.location.search)
		const filtersParam = searchParams.get('filters')

		if (filtersParam) {
			try {
				const decodedFilters = decodeFilters<FilterOption[]>(filtersParam)
				if (decodedFilters && Array.isArray(decodedFilters) && decodedFilters.length > 0) {
					onFiltersChange(decodedFilters)
				}
			} catch (error) {
				console.error('Failed to parse URL filters:', error)
			}
		}

		// Immediately mark as initialized to allow Sync effect to take over
		setIsInitialized(true)
	}, [insideModal, onFiltersChange, isInitialized])

	// 2. Bidirectional sync
	useEffect(() => {
		if (insideModal || !isInitialized) {
			return
		}

		const urlFiltersString = search.filters
		const parsedUrlFilters = decodeFilters<FilterOption[]>(urlFiltersString) || []

		// Use deep equality check to prevent infinite navigation loops
		if (!isEqual(filters, parsedUrlFilters)) {
			// CRITICAL: Protection against "reset on mount" patterns in parent components.
			// Some components reset their state to [] on mount after the initial URL load effect has run.
			// This check re-applies the URL filters to local state instead of letting the empty state clear the URL.
			if (filters.length === 0 && parsedUrlFilters.length > 0 && isFirstSyncRef.current) {
				if (onFiltersChange) {
					onFiltersChange(parsedUrlFilters)
				}
				isFirstSyncRef.current = false
				return
			}

			isFirstSyncRef.current = false
			const encoded = encodeFilters(filters.length > 0 ? filters : undefined)

			if (encoded) {
				const searchParams = new URLSearchParams(window.location.search)
				searchParams.set('filters', encoded)
				const fullUrlLength =
					window.location.origin.length + window.location.pathname.length + 1 + searchParams.toString().length
				if (fullUrlLength > URL_CHARACTER_LIMIT) {
					if (filterLimitFlags.justWarnedPasteLimit) {
						hasWarnedRef.current = true
					} else if (!hasWarnedRef.current) {
						toast.warning(
							'The filter data is very large. This link may not be shareable because the URL length exceeds the standard character limit.',
						)
						hasWarnedRef.current = true
					}
				} else {
					hasWarnedRef.current = false
				}
				filterLimitFlags.justWarnedPasteLimit = false
			} else {
				hasWarnedRef.current = false
			}

			navigate({
				to: currentPathname,
				search: (prev: Record<string, unknown>) => ({
					...prev,
					filters: encoded || undefined,
				}),
				replace: true,
			} as never)
		} else if (filters.length > 0) {
			isFirstSyncRef.current = false
		}
	}, [filters, insideModal, isInitialized, navigate, search.filters, onFiltersChange, currentPathname])

	// 3. Section change detection: Clear filters if moving to a new section/tab
	useEffect(() => {
		if (typeof window === 'undefined' || !('sessionStorage' in window)) {
			return
		}

		const currentSection = getRouteSection(currentPathname)
		const lastKnownSection = window.sessionStorage.getItem(SECTION_STORAGE_KEY)

		// If we detect the section has changed from the last known one, clear filters.
		if (lastKnownSection && currentSection !== lastKnownSection) {
			const hasFiltersInUrl = !!search.filters

			if (hasFiltersInUrl) {
				navigate({
					to: currentPathname,
					search: (prev: Record<string, unknown>) => ({
						...prev,
						filters: undefined,
					}),
					replace: true,
				} as never)

				if (onFiltersChange) {
					onFiltersChange([])
				}
			}
		}

		// Update the session storage for the last known section
		window.sessionStorage.setItem(SECTION_STORAGE_KEY, currentSection)
	}, [currentPathname, navigate, onFiltersChange, search.filters])
}
